import React from 'react';
import PropTypes from 'prop-types';
import styles from './styles/styles.module.scss';

import Checkbox from '../../components/forms/Checkbox';
import SlidingTabs from '../../components/shared/SlidingTabs/SlidingTabs';
import { ServerCommunicationUtil } from '../../utils/server_communication_util';
import { wrapEventHandler } from '../../utils/handlers';

const bulkTabs = [
  { label: "Tags", key: "tags" },
  { label: "Alerts", key: "alerts" }
];

const priorityTags = [
  'closed',
  'fee',
  'dogs',
  'dogs-no',
  'dogs-leash'
];

const mutuallyExclusiveTags = {
  'dogs': 'dog-tags',
  'dogs-no': 'dog-tags',
  'dogs-leash': 'dog-tags'
};

const noAlert = "[No Alert]";

class BulkTrailEdit extends React.Component {
  constructor(props) {
    super(props);
    const { trails } = this.props;
    const { type } = this.props.location;
    const typeDisplay = type === "area" ? "park" : type;
    this.state = {
      activeIndex: 0,
      alertsText: null,
      typeDisplay,
      trails,
      changes: {},
      selectedTrails: this.allTrailsHash(),
      alertsTrails: this.buildAlertsTrailsList(this.props.trails, {}),
      highlightedTag: null,
      applyingChanges: false,
      changesApplied: false
    };
  };

  componentDidMount() {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', e => {
        if (this.countChangesToApply()) {
          e.preventDefault();
          e.returnValue = `Warning: ${this.state.typeDisplay} has not been refreshed since last bulk action.`;
        }
      });
    }
  };

  countChangesToApply() {
    let { changes } = this.state;
    let count = 0;
    let changeCounts = {
      "tags": 0,
      "alerts": 0
    }
    Object.keys(changes).forEach(trailId => {
      if (changes[trailId].tags) {
        count += changes[trailId].tags.add.length + changes[trailId].tags.sub.length;
        changeCounts["tags"] += changes[trailId].tags.add.length + changes[trailId].tags.sub.length;
      }
      if ("alerts" in changes[trailId]) {
        count++;
        changeCounts["alerts"]++;
      }
     });
    return count > 0 ? changeCounts : null;
  };

  handleTrailSelectChange(trailId) {
    let { selectedTrails } = this.state;
    selectedTrails[trailId] = !selectedTrails[trailId];
    this.setState({ selectedTrails });
  };

  countSelectedTrails() {
    return Object.values(this.state.selectedTrails).filter(Boolean).length;
  };

  addRemoveTrailTag(changes, trail, tagUid, add) {
    const trailId = trail.id;
    const trailHasThisTag = this.trailHasTag(trail, tagUid);
    let tags = changes[trailId] && changes[trailId].tags ? changes[trailId].tags : { add:[], sub:[]};
    if (add && !tags.add.includes(tagUid) && (!trailHasThisTag || tags.sub.includes(tagUid))) {
      if (tags.sub.includes(tagUid)) {
        tags.sub = tags.sub.filter(uid => uid !== tagUid);
      } else {
        tags.add.push(tagUid);
      }
    } else if (!add && !tags.sub.includes(tagUid) && (trailHasThisTag || tags.add.includes(tagUid))) {
      if (tags.add.includes(tagUid)) {
        tags.add = tags.add.filter(uid => uid !== tagUid);
      } else {
        tags.sub.push(tagUid);
      }
    }
    changes[trailId] = Object.assign({}, changes[trailId], { tags });
  };

  tagClick(tag) {
    const highlightedTag = tag === this.state.highlightedTag ? null : tag;
    this.setState({ highlightedTag });
  };

  addRemoveTag(tag, add) {
    let { changes, selectedTrails, trails } = this.state;
    trails.forEach(trail => {
      if (selectedTrails[trail.id]) {
        this.addRemoveTrailTag(changes, trail, tag.uid, add);
        // If adding a tag in a mutually-exclusive set (eg the dog-related tags) then remove any other tags in the set:
        if (add && mutuallyExclusiveTags[tag.uid]) {
          Object.keys(mutuallyExclusiveTags).forEach(otherTagUid => {
            if (otherTagUid !== tag.uid && mutuallyExclusiveTags[otherTagUid] === mutuallyExclusiveTags[tag.uid]) {
              this.addRemoveTrailTag(changes, trail, otherTagUid, false);
            }
          })
        }
      }
    });
    this.setState({ changes, changesApplied: false });
  };

  allTrailsHash() {
    let selectedTrails = {};
    this.props.trails.forEach(trail => selectedTrails[trail.id] = true);
    return selectedTrails;
  };

  selectAllNone(all) {
    let selectedTrails = all ? this.allTrailsHash() : {};
    this.setState({ selectedTrails });
  };

  currentAlertsTextShown(trail, changes) {
    // changes[trail.id].alerts is present but null if user has cleared the alerts
    return changes[trail.id] && ("alerts" in changes[trail.id]) ? changes[trail.id].alerts : trail.alerts;
  };

  selectAlertsTrails(alertsText) {
    const { changes } = this.state;
    let selectedTrails = {};
    if (alertsText === noAlert) {
      alertsText = null;
    }
    this.props.trails.forEach(trail => selectedTrails[trail.id] = this.currentAlertsTextShown(trail, changes) === alertsText);
    this.setState({ selectedTrails });
  };

  buildAlertsTrailsList(trails, changes) {
    let alertsTrails = {};
    trails.forEach(trail => {
      const alertsText = this.currentAlertsTextShown(trail, changes) || noAlert;
      if (alertsTrails[alertsText]) {
        alertsTrails[alertsText].push(trail.id);
      } else {
        alertsTrails[alertsText] = [trail.id];
      }
    });
    return alertsTrails;
  };

  trailHasTag(trail, tagUid) {
    return trail.attributes.activities.find(attr => attr.uid === tagUid) ||
      trail.attributes.features.find(attr => attr.uid === tagUid) ||
      trail.attributes.obstacles.find(attr => attr.uid === tagUid);
  };

  offerAddSub(tagUid) {
    // Offer add button unless all selected trails:
    // - have it, and there it not a "sub" set for it
    // - has an "add" set for it
    // Reverse for remove button
    const { changes, selectedTrails, trails } = this.state;
    let count = 0;
    let offerAdd = false;
    let offerSub = false;
    trails.forEach(trail => {
      if (selectedTrails[trail.id]) {
        const trailHasThisTag = this.trailHasTag(trail, tagUid);
        const hasAdd = changes[trail.id] && changes[trail.id].tags && changes[trail.id].tags.add.includes(tagUid);
        const hasSub = changes[trail.id] && changes[trail.id].tags && changes[trail.id].tags.sub.includes(tagUid);
        if ((!trailHasThisTag && !hasAdd) || (trailHasThisTag && hasSub)) {
          offerAdd = true;
        }
        if ((trailHasThisTag && !hasSub) || (!trailHasThisTag && hasAdd)) {
          count++;
          offerSub = true;
        }
      }
    });
    return { count, offerAdd, offerSub };
  };

  setSelectedTrailsAlerts(alerts) {
    let { changes, selectedTrails, trails } = this.state;
    trails.forEach(trail => {
      if (selectedTrails[trail.id]) {
        changes[trail.id] = Object.assign({}, changes[trail.id], { alerts });
      }
    });
    const alertsTrails = this.buildAlertsTrailsList(trails, changes);
    this.setState({ alertsTrails, changes, changesApplied: false });
  };

  applyChanges() {
    if (!confirm("Are you sure you want to submit these trail tag changes?")) {
      return;
    }
    this.setState({ applyingChanges: true, changesApplied: false });
    const params = {
      type: this.props.location.type,
      id: this.props.location.id,
      data: {
        action: "trail-edits",
        changes: this.state.changes
      }
    }
    ServerCommunicationUtil.postApiEndpoint(
      "/api/alltrails/admin/bulk_trail_edit",
      params,
      () => {
        this.mergeChanges();
        this.setState({ applyingChanges: false, changesApplied: true });
        this.queueLocationRefresh();
      },
      () => {},
      () => {}
    );
  };

  queueLocationRefresh() {
    ServerCommunicationUtil.getApiEndpoint(
      "/api/alltrails/locations/refresh_location_cache",
      {
        location_id: this.props.location.id,
        location_type: this.props.location.type,
        refresh_trails: true
      },
      () => {},
      () => {},
      () => {}
    );
  };

  getTagSections() {
    const { trailTags } = this.props;
    let tagSections = {};
    Object.keys(trailTags).forEach(section => {
      trailTags[section].forEach(tag => {
        tagSections[tag.uid] = {
          section,
          name: tag.name
        }
      });
    });
    return tagSections;
  };

  mergeChanges() {
    let { changes, trails } = this.state;
    const tagSections = this.getTagSections();
    trails.forEach(trail => {
      if (changes[trail.id]) {
        if (changes[trail.id].tags) {
          changes[trail.id].tags.add.forEach(tagUid => {
            trail.attributes[tagSections[tagUid].section].push({
              uid: tagUid,
              name: tagSections[tagUid].name
            });
          });
          changes[trail.id].tags.sub.forEach(tagUid => {
            trail.attributes[tagSections[tagUid].section] =
              trail.attributes[tagSections[tagUid].section].filter(tag => tag.uid !== tagUid);
          });
        }
        if ("alerts" in changes[trail.id] && trail.alerts !== changes[trail.id].alerts) {
          trail.alerts = changes[trail.id].alerts;
        }
      };
    });
    this.setState({ changes: {}, trails });
  };

  handleTrailTagClick(trail, tagUid, wasAdd) {
    let { changes } = this.state;
    this.addRemoveTrailTag(changes, trail, tagUid, !wasAdd);
    this.setState({ changes });
  };

  handleTabChange = (e, activeIndex) => {
    e.preventDefault();
    if (activeIndex !== this.state.activeIndex) {
      this.setState({ activeIndex });
    }
  };

  handleAlertsTextChange(e) {
    this.setState({ alertsText: e.currentTarget.value });
  }

  createTrailTagButton(trail, tagUid, isAdd) {
    const classes = `btn-slim brown option-select ${isAdd ? '' : styles.tagDelete}`;
    return (
      <a key={tagUid} className={classes} onClick={() => this.handleTrailTagClick(trail, tagUid, isAdd)}>
        {tagUid}
      </a>
    );
  };

  renderLocation() {
    const { location } = this.props;
    let refreshLink = null;
    if (this.countChangesToApply()) {
      if (this.state.applyingChanges) {
        refreshLink = <span>Updating trails...</span>;
      } else {
        refreshLink = (
          <button onClick={wrapEventHandler(() => this.applyChanges())}>Submit</button>
        );
      }
    } else if (this.state.changesApplied) {
      refreshLink = <span>Trails updated and location refresh queued</span>;
    }
    const selectedCount = this.countSelectedTrails();
    const selectAllButton = (
      <button onClick={wrapEventHandler(() => this.selectAllNone(true))} disabled={selectedCount === this.state.trails.length}>
        All
      </button>
    );
    const selectNoneButton = (
      <button onClick={wrapEventHandler(() => this.selectAllNone(false))} disabled={selectedCount === 0}>
        None
      </button>
    );
    return (
      <table className={styles.location}>
        <tbody>
          <tr>
            <th>{this.state.typeDisplay}:</th>
            <td><a href={`/${location.url}`} target="_blank">{location.name}</a></td>
          </tr>
          <tr>
            <th>Trails:</th>
            <td>{location.trailCount}</td>
          </tr>
          <tr className={styles.buttonRow}>
            <td></td>
            <td>
              {selectAllButton}
              {selectNoneButton}
              {refreshLink}
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  renderTagTrailChanges(trail, changes) {
    if (changes[trail.id] && changes[trail.id].tags) {
      return [
        ...changes[trail.id].tags.add.map(uid => this.createTrailTagButton(trail, uid, true)),
        ...changes[trail.id].tags.sub.map(uid => this.createTrailTagButton(trail, uid, false))
      ];
    }
    return null;
  };

  renderHighlightedTag(trail, highlightedTag) {
    if (highlightedTag && this.trailHasTag(trail, highlightedTag.uid)) {
      return (
        <span className="tag">
          <span className={`small rounded active ${styles.highlighted}`}>{highlightedTag.name}</span>
        </span>
      );
    }
    return null;
  };

  renderAlertsTrailChanges(trail, changes) {
    const alertsText = this.currentAlertsTextShown(trail, changes);
    const newAlerts = (changes[trail.id] && "alerts" in changes[trail.id]) ? changes[trail.id].alerts : undefined;
    if (trail.alerts || alertsText) {
      if (trail.alerts === alertsText) {
        return (
          <span>{trail.alerts}</span>
        );
      }
      return (
        <span>
          <span className={styles.deletedText}>{trail.alerts}</span>
          <span className={styles.addedText}>{alertsText}</span>
        </span>
      );
    }
    return null;
  };

  renderTrails() {
    const { changes, highlightedTag, selectedTrails, trails } = this.state;
    const trailRows = trails.map(trail => {
      let showChanges = null;
      let showHighlightedTag = null;
      switch (bulkTabs[this.state.activeIndex].key) {
        case "tags":
          showChanges = this.renderTagTrailChanges(trail, changes);
          showHighlightedTag = this.renderHighlightedTag(trail, highlightedTag);
          break;
        case "alerts":
          showChanges = this.renderAlertsTrailChanges(trail, changes);
          break;
      }
      return (
        <tr key={trail.id}>
          <td>
            <Checkbox
              handleChange={() => this.handleTrailSelectChange(trail.id)}
              checked={selectedTrails[trail.id]}
              label={`${trail.id}`}
            />
          </td>
          <td><a href={`/trail/${trail.slug}`} target="_blank">{trail.name}</a></td>
          <td>{showHighlightedTag}{showChanges}</td>
        </tr>
      );
    })
    return (
      <table className={styles.trailList}>
        <tbody>
          {trailRows}
        </tbody>
      </table>
    );
  };

  renderTag(tag) {
    const { count, offerAdd, offerSub } = this.offerAddSub(tag.uid);
    const classes = `small rounded active ${tag === this.state.highlightedTag ? styles.highlighted : ''}`;
    const tagSpan = (
      <span className="tag">
        <span className={classes} onClick={wrapEventHandler(()=>this.tagClick(tag))}>{`${tag.name}${count ? ` (${count})` : ''}`}</span>
      </span>
    );
    const addButton = offerAdd ?
      <button onClick={wrapEventHandler(()=>this.addRemoveTag(tag, true))}>Add</button> : null;
    const removeButton = offerSub ?
      <button onClick={wrapEventHandler(()=>this.addRemoveTag(tag, false))}>Remove</button> : null;
    return (
      <tr key={tag.uid}>
        <td>{tagSpan}</td>
        <td>{addButton}</td>
        <td>{removeButton}</td>
      </tr>
    );
  };

  renderPriorityTags() {
    const { trailTags } = this.props;
    let tags = [];
    priorityTags.forEach(tagUid => {
      Object.values(trailTags).forEach(section => {
        section.forEach(tag => {
          if (tag.uid === tagUid) {
            tags.push(this.renderTag(tag));
          }
        })
      })
    });
    return tags;
  };

  renderTagSection(section) {
    const { trailTags } = this.props;
    let tags = [];
    trailTags[section].map(tag => {
      if (priorityTags.indexOf(tag.uid) === -1) {
        tags.push(this.renderTag(tag));
      }
    });
    return tags;
  };

  renderLeftPanel() {
    switch (bulkTabs[this.state.activeIndex].key) {
      case "tags":
        return this.renderTags();
      case "alerts":
        return this.renderAlerts();
    }
  };

  renderTags() {
    return (
      <table>
        <tbody>
          {this.renderPriorityTags()}
          <tr><td className={styles.divider} colSpan="3"></td></tr>
          {this.renderTagSection("activities")}
          <tr><td className={styles.divider} colSpan="3"></td></tr>
          {this.renderTagSection("features")}
          <tr><td className={styles.divider} colSpan="3"></td></tr>
          {this.renderTagSection("obstacles")}
        </tbody>
      </table>
    );
  };

  renderAlerts() {
    const alertsRows = Object.keys(this.state.alertsTrails).map((alerts, i) => (
      <p key={i}><a onClick={wrapEventHandler(() => this.selectAlertsTrails(alerts))}>{alerts}</a></p>
    ));

    const selectedCount = this.countSelectedTrails();
    const allowClearSelected = selectedCount > 0;
    const allowSetAlerts = selectedCount > 0 && this.state.alertsText !== null && this.state.alertsText !== "";
    return (
      <div>
        <textarea rows="5" placeholder="Enter new alerts text"                 defaultValue={this.state.alertsText}
                onChange={e => this.handleAlertsTextChange(e)}></textarea>
        <div className={styles.buttonRow}>
          <button onClick={wrapEventHandler(() => this.setSelectedTrailsAlerts(this.state.alertsText))} disabled={!allowSetAlerts}>Set Alert on Selected</button>
          <button onClick={wrapEventHandler(() => this.setSelectedTrailsAlerts(null))} disabled={!allowClearSelected}>Clear Selected</button>
          <p>Click on an alert to select its matching trails</p>
        </div>
        <div>
         {alertsRows}
        </div>
      </div>
    );
  };

  renderSlidingTabs() {
    const changeCounts = this.countChangesToApply();
    let tabs = bulkTabs.map(tab => {
      const label = `${tab.label}${changeCounts && changeCounts[tab.key] ? ` (${changeCounts[tab.key]})`: ""}`;
      return { ...tab, label };
    });
    return (
      <SlidingTabs
        onChange={this.handleTabChange}
        activeIndex={this.state.activeIndex}
        slidingTabsClasses={{ root: styles.slidingTabsRoot }}
        tabs={tabs}
      />
    );
  }

  render() {
    return (
      <div className={`${styles.body} xlate-none`}>
        <div className={styles.locationPanel}>{this.renderLocation()}</div>
        {this.renderSlidingTabs()}
        <div className={styles.content}>
          <div className={styles.leftPanel}>{this.renderLeftPanel()}</div>
          <div className={styles.trailsPanel}>{this.renderTrails()}</div>
        </div>
      </div>
    );
  }
};

const tagPropShape = PropTypes.arrayOf(PropTypes.shape({
  name: PropTypes.string,
  uid: PropTypes.string.isRequired
})).isRequired;

const attributeSectionsShape = PropTypes.shape({
  activities: tagPropShape,
  features: tagPropShape,
  obstacles: tagPropShape
});

BulkTrailEdit.propTypes = {
  location: PropTypes.shape({
    type: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    trailCount: PropTypes.number.isRequired
  }).isRequired,
  trails: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    slug: PropTypes.string,
    name: PropTypes.string,
    attributes: attributeSectionsShape.isRequired
  })).isRequired,
  trailTags: attributeSectionsShape
};

export default BulkTrailEdit;
